import { Alert } from '@mui/material'
import { useT } from '@/i18n/useT'
import type { ProcessingJob } from '@/types/invoiceAnalyser'

interface ProcessingStatusProps {
  job: ProcessingJob
  /** Name of the uploaded file — the job itself does not carry it */
  filename?: string | null
}

/**
 * Progress as the design shows it: a checklist rather than a bar, so it is
 * clear which stage the extraction is actually in. Each step owns a progress
 * threshold; the job's numeric progress decides done / running / pending.
 */
const STEPS: { at: number; label: string }[] = [
  { at: 10, label: 'Document uploaded' },
  { at: 20, label: 'Document type recognised' },
  { at: 50, label: 'Data extracted' },
  { at: 70, label: 'Extraction checked' },
  { at: 100, label: 'Result ready' },
]

export function ProcessingStatus({ job, filename }: ProcessingStatusProps) {
  const { t } = useT()
  const progress = job.status === 'completed' ? 100 : job.progress

  return (
    <div
      style={{
        flex: '1 1 340px',
        minWidth: 0,
        background: 'var(--paper)',
        border: '1px solid var(--line)',
        borderRadius: 12,
        padding: 18,
      }}
    >
      <div style={{ fontSize: 13, fontWeight: 700 }}>
        {job.status === 'failed' ? t('Processing failed') : t('Processing')}
        {filename ? ` — ${filename}` : ''}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, marginTop: 14 }}>
        {STEPS.map((step, i) => {
          const done = progress >= step.at
          const previousAt = i === 0 ? 0 : STEPS[i - 1].at
          const running = !done && progress >= previousAt && job.status === 'processing'

          const fg = done ? 'var(--onAccent)' : running ? 'var(--warn)' : 'var(--faint)'
          const bg = done ? 'var(--accent)' : running ? 'var(--warnSoft)' : 'transparent'
          const border = done
            ? 'var(--accent)'
            : running
              ? 'var(--warn)'
              : 'var(--line)'

          return (
            <div
              key={step.label}
              style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 0' }}
            >
              <div
                style={{
                  width: 22,
                  height: 22,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 11,
                  fontWeight: 700,
                  flexShrink: 0,
                  background: bg,
                  color: fg,
                  border: `1px solid ${border}`,
                }}
              >
                {done ? '✓' : i + 1}
              </div>
              <div
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  flex: 1,
                  color: done || running ? 'var(--text)' : 'var(--faint)',
                }}
              >
                {t(step.label)}
              </div>
              <div style={{ fontSize: 12, color: 'var(--muted)' }}>
                {done ? t('done') : running ? job.progressMessage || t('running …') : ''}
              </div>
            </div>
          )
        })}
      </div>

      {job.status === 'failed' && job.error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {job.error}
        </Alert>
      )}
    </div>
  )
}
